import { useEffect, useRef, type ReactNode } from 'react';
import { ClipboardCheck, FlaskConical, X } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tr,
  badgeStyles,
} from '@/components/ui';
import {
  PLACEHOLDER,
  formatDMS,
  formatFixed,
  formatScale,
  scaleToPpm,
} from '@/logic/statistics';
import { useProgressStore, selectIsProcessing } from '@/store/progressStore';
import type { TransformationSolution } from '@/types';
import type { RejectionTrialPreview } from '@/types/adjustmentReview';
import { largestActiveResidual, pairLabelOf } from './adjustmentDisplay';
import { SigmaZero } from './Symbols';

const signedDelta = (delta: number | null, decimals: number, suffix = '') =>
  delta === null || !Number.isFinite(delta)
    ? PLACEHOLDER
    : `${formatFixed(delta, decimals, { signed: true })}${suffix}`;

/** Difference of two angles in degrees, wrapped to (−180°, 180°]. */
const angleDifference = (after: number, before: number) => {
  const delta = (after - before) % 360;
  return delta > 180 ? delta - 360 : delta <= -180 ? delta + 360 : delta;
};

interface Measure {
  key: string;
  label: ReactNode;
  before: string;
  after: string;
  change: string;
}

const measures = (
  accepted: TransformationSolution | null,
  trial: TransformationSolution
): Measure[] => {
  const fit = accepted && !accepted.error ? accepted : null;
  const statistic = (solution: TransformationSolution, value?: number) =>
    solution.redundancy > 0 ? (value ?? null) : null;
  const pair = (
    key: string,
    label: ReactNode,
    pick: (solution: TransformationSolution) => number | null,
    decimals: number
  ): Measure => {
    const before = fit ? pick(fit) : null;
    const after = pick(trial);
    return {
      key,
      label,
      before: formatFixed(before, decimals),
      after: formatFixed(after, decimals),
      change:
        before === null || after === null
          ? PLACEHOLDER
          : signedDelta(after - before, decimals),
    };
  };
  return [
    pair('rms', 'RMS', (s) => statistic(s, s.rmsTotal), 4),
    pair(
      'largest',
      'Largest residual in fit',
      (s) => statistic(s, largestActiveResidual(s)?.total ?? undefined),
      4
    ),
    pair(
      'sigma0',
      <>
        Unit weight <SigmaZero />
      </>,
      (s) => s.sigma0 ?? null,
      4
    ),
    {
      key: 'pairs',
      label: 'Pairs in fit · redundancy',
      before: fit ? `${fit.pointCount} · r ${fit.redundancy}` : PLACEHOLDER,
      after: `${trial.pointCount} · r ${trial.redundancy}`,
      change: fit
        ? signedDelta(trial.redundancy - fit.redundancy, 0, ' r')
        : PLACEHOLDER,
    },
    {
      key: 'rotation',
      label: 'Rotation (clockwise)',
      before: fit ? formatDMS(fit.rotation) : PLACEHOLDER,
      after: formatDMS(trial.rotation),
      change: fit
        ? signedDelta(
            angleDifference(trial.rotation, fit.rotation) * 3600,
            1,
            '"'
          )
        : PLACEHOLDER,
    },
    {
      key: 'scale',
      label: 'Scale',
      before: fit ? formatScale(fit.scale) : PLACEHOLDER,
      after: formatScale(trial.scale),
      change: fit
        ? signedDelta(
            scaleToPpm(trial.scale) - scaleToPpm(fit.scale),
            1,
            ' ppm'
          )
        : PLACEHOLDER,
    },
  ];
};

/**
 * A computed rejection trial beside the accepted fit. Nothing is accepted
 * until "Apply rejection and recompute"; the field-evidence prompt stays in
 * front of that action.
 */
export const TrialReview = ({
  trial,
  solution,
  solutionStale,
  onApply,
  onDismiss,
  onSelectPoint,
}: {
  trial: RejectionTrialPreview;
  solution: TransformationSolution | null;
  solutionStale: boolean;
  onApply: () => void;
  onDismiss: () => void;
  onSelectPoint: (id: number) => void;
}) => {
  const busy = useProgressStore(selectIsProcessing);
  const previewRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (busy) return;
    previewRef.current?.focus({ preventScroll: true });
    previewRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [trial, busy]);

  const labels = trial.rejectedIds.map((id) =>
    pairLabelOf(trial.pointPairs, id)
  );
  const count = trial.rejectedIds.length;

  return (
    <Card
      ref={previewRef}
      tabIndex={-1}
      role="region"
      aria-label="Rejection trial"
      className="overflow-hidden border-accent/50 ring-1 ring-accent/25"
    >
      <div className="flex flex-wrap items-start gap-3 px-4 pt-4 sm:px-5">
        <span
          aria-hidden="true"
          className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent-muted text-accent [&_svg]:size-4"
        >
          <FlaskConical />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-fg">Rejection trial</h2>
            <Badge tone="accent">Preview · not applied</Badge>
          </div>
          <p role="status" className="text-sm text-fg-muted">
            The table and map show this trial with{' '}
            {count === 1 ? 'one pair' : `${count} pairs`} set to Free. Accepted
            weights and the solution stay unchanged until you apply it.
          </p>
        </div>
      </div>
      <CardBody className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-fg-muted">
            Proposed Free
          </span>
          {trial.rejectedIds.map((id, index) => (
            <button
              key={id}
              type="button"
              aria-label={`Show proposed pair ${labels[index]}`}
              onClick={() => onSelectPoint(id)}
              className={badgeStyles({
                tone: 'accent',
                size: 'md',
                interactive: true,
                className: 'font-mono',
              })}
            >
              {labels[index]}
            </button>
          ))}
        </div>
        {solutionStale && (
          <Alert tone="warning">
            The accepted fit reflects previous inputs. This trial uses your
            current edits.
          </Alert>
        )}
        <div className="overflow-hidden rounded-lg border border-border">
          <Table>
            <caption className="sr-only">
              Accepted solution and rejection trial comparison
            </caption>
            <THead>
              <Tr>
                <Th className="px-2 sm:px-3">Measure</Th>
                <Th numeric className="px-2 sm:px-3">
                  {solutionStale ? 'Previous inputs' : 'Current'}
                </Th>
                <Th numeric className="px-2 sm:px-3">
                  Trial
                </Th>
                <Th numeric className="hidden sm:table-cell">
                  Change
                </Th>
              </Tr>
            </THead>
            <TBody className="[&>tr:last-child>th]:border-b-0">
              {measures(solution, trial.solution).map((row) => (
                <Tr key={row.key}>
                  <Th
                    scope="row"
                    className="h-9 whitespace-normal bg-transparent px-2 text-xs font-normal normal-case tracking-normal text-fg-muted sm:px-3 sm:text-sm"
                  >
                    {row.label}
                  </Th>
                  <Td
                    numeric
                    className="h-9 whitespace-nowrap px-2 text-xs text-fg-muted sm:px-3 sm:text-sm"
                  >
                    {row.before}
                  </Td>
                  <Td
                    numeric
                    className="h-9 whitespace-nowrap px-2 text-xs font-medium sm:px-3 sm:text-sm"
                  >
                    {row.after}
                  </Td>
                  <Td
                    numeric
                    className="hidden h-9 whitespace-nowrap text-fg-muted sm:table-cell"
                  >
                    {row.change}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </div>
        <Alert
          tone="neutral"
          icon={<ClipboardCheck />}
          title="Review field evidence before applying"
        >
          Check correspondence, monument condition, and field notes for{' '}
          {labels.join(', ')}. A smaller residual alone does not show that a
          monument or match is wrong. Large rotation or scale changes can mean
          the wrong pair is being rejected.
        </Alert>
      </CardBody>
      <CardFooter className="max-sm:flex-col-reverse max-sm:items-stretch">
        <Button leadingIcon={<X />} onClick={onDismiss}>
          Discard trial
        </Button>
        <Button variant="primary" loading={busy} onClick={onApply}>
          Apply rejection and recompute
        </Button>
      </CardFooter>
    </Card>
  );
};
